import { useCallback, useContext, useEffect, useRef, type MutableRefObject, type ReactNode } from "react";
import { UNSAFE_DataRouterContext, useBlocker } from "react-router-dom";

/** 未保存草稿离开页面时的统一确认文案。 */
export const UNSAVED_CHANGES_MESSAGE = "当前有未保存修改，离开后未保存修改将丢失。确认离开吗？";

type UnsavedChangesBlockerProps = {
  when: boolean;
  bypassNextNavigationRef: MutableRefObject<boolean>;
};

/** 仅在数据路由上下文中挂载 SPA 导航阻断器。 */
function UnsavedChangesBlocker({
  when,
  bypassNextNavigationRef,
}: UnsavedChangesBlockerProps) {
  const blocker = useBlocker(() => {
    if (!when) return false;
    if (bypassNextNavigationRef.current) {
      bypassNextNavigationRef.current = false;
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm(UNSAVED_CHANGES_MESSAGE)) {
      blocker.proceed();
      return;
    }
    blocker.reset();
  }, [blocker]);

  return null;
}

export type UnsavedChangesGuard = {
  blocker: ReactNode;
  runWithNavigationBypass: (navigate: () => void) => void;
};

/**
 * 为脏草稿提供 SPA 路由、浏览器后退及刷新关闭保护。
 * 已由业务弹窗确认的内部跳转可使用一次性 bypass，避免重复确认。
 */
export function useUnsavedChangesGuard(when: boolean): UnsavedChangesGuard {
  const dataRouterContext = useContext(UNSAFE_DataRouterContext);
  const bypassNextNavigationRef = useRef(false);

  useEffect(() => {
    if (!when) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = UNSAVED_CHANGES_MESSAGE;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [when]);

  const runWithNavigationBypass = useCallback((navigate: () => void) => {
    bypassNextNavigationRef.current = true;
    try {
      navigate();
    } finally {
      queueMicrotask(() => {
        bypassNextNavigationRef.current = false;
      });
    }
  }, []);

  return {
    blocker: dataRouterContext ? (
      <UnsavedChangesBlocker
        when={when}
        bypassNextNavigationRef={bypassNextNavigationRef}
      />
    ) : null,
    runWithNavigationBypass,
  };
}
